import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { MutationStatus, useQueryClient } from '@tanstack/react-query';
import { post2faCodeResponse } from '../../../api/calls/post2faCodeApiCall';
import { PostTokenBasicResponse } from '../../../api/calls/postTokenBasicApiCall';
import { useSessionStorage } from 'usehooks-ts';
import { ValidationError } from '../../../api/errors/ValidationError';
import dayjs from 'dayjs';
import useBasicTokenMutation from 'ui/hooks/useBasicTokenMutation';
import useDeleteTokenMutation from 'ui/hooks/useDeleteTokenMutation';
import useErrorMessage from './useErrorMessage';
import useFingerprint from 'hooks/useFingerprint';
import useSend2faCodeMutation from '../../hooks/useSend2faCodeMutation';
import useSend2faEMAILMutation from '../../hooks/useSend2faEMAILMutation';
import useTemporaryPasswordMutation from 'ui/hooks/useTemporaryPasswordMutation';

export type TYPE_ERROR_CODE_EMAIL_MESSAGE =
  | 'GENERIC_2FA_ERROR'
  | 'VERIFICATION_ID_NOT_CREATED'
  | 'CLIENT_NOT_FOUND_BY_CLIENT_NUMBER'
  | 'CLIENT_NUMBER_NOT_FOUND_BY_VERIFICATION_ID'
  | 'VERIFICATION_ID_NOT_FOUND_BY_CODE_ID_AND_CODE_INCORRECT_CODE'
  | 'NOTIFICATION_SENDER_NOT_FOUND'
  | 'CODE_NOT_MATCHED'
  | 'TOO_MANY_ATTEMPTS'
  | 'GENERIC';

export const ERROR_2FA_SESSION_EXPIRED: TYPE_ERROR_CODE_EMAIL_MESSAGE[] = [
  'VERIFICATION_ID_NOT_CREATED',
  'CLIENT_NOT_FOUND_BY_CLIENT_NUMBER',
  'CLIENT_NUMBER_NOT_FOUND_BY_VERIFICATION_ID',
  'VERIFICATION_ID_NOT_FOUND_BY_CODE_ID_AND_CODE_INCORRECT_CODE',
];

export interface AuthContextValue {
  login: (
    emailOrPersonalID: string,
    password: string,
  ) => Promise<PostTokenBasicResponse>;
  logout: () => void;
  triggerUnauthorized: () => void;
  triggerForbidden: () => void;
  recoverByEmail: (email: string) => void;
  recoverPasswordStatus: MutationStatus;
  loggingInStatus: MutationStatus;
  loginError: string | null;
  isLoggedIn: boolean;
  fromTemporaryPassword: boolean;
  emailOrPersonalId: string | null;
  token: string | null;
  isForbidden: boolean;
  verificationId: string | null;
  // 2fa
  secondsLeft2fa: number;
  fetch2faEMAIL: () => Promise<void>;
  fetch2faCode: (code: string) => Promise<void>;
  error2faCode: TYPE_ERROR_CODE_EMAIL_MESSAGE | undefined;
  status2faCode: string | undefined;
  clear2faStates: () => void;
  error2faEmail: TYPE_ERROR_CODE_EMAIL_MESSAGE | undefined;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_2FA_TIME_KEY = '2faCodeTime';
const SECONDS_GAP_BETWEEN_CALLS = 60;

const AuthProvider: FC<PropsWithChildren> = (props) => {
  const [token, setToken] = useSessionStorage<string | null>('token', null);
  const [emailOrPersonalId, setEmailOrPersonalId] = useSessionStorage<
    string | null
  >('emailOrPersonalId', null);
  const [fromTemporaryPassword, setFromTemporaryPassword] =
    useSessionStorage<boolean>('fromTemporaryPassword', false);
  const [verificationId, setVerificationId] = useSessionStorage<string | null>(
    'verificationId',
    null,
  );
  const [twoFACode, setTwoFACode] = useSessionStorage<string | null>(
    'twoFACode',
    null,
  );
  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(token));
  const [isForbidden, setIsForbidden] = useState(false);

  const queryClient = useQueryClient();
  const deleteTokenMutation = useDeleteTokenMutation();
  const {
    mutateAsync: fetchBasicToken,
    error: basicTokenError,
    status: basicTokenStatus,
  } = useBasicTokenMutation();
  const {
    mutateAsync: mutateAsyncEMAIL,
    status: status2faEmail,
    error: error2faEmail,
    reset: reset2faEmail,
  } = useSend2faEMAILMutation();
  const {
    mutateAsync: mutateAsyncCode,
    status: status2faCode,
    error: error2faCode,
    reset: reset2faCode,
  } = useSend2faCodeMutation();
  const passwordRecoveryMutation = useTemporaryPasswordMutation();
  const loginError = useErrorMessage(basicTokenError);
  const fingerprint = useFingerprint();

  // Read last value inside setInterval function
  const refStatus2faEmail = useRef(status2faEmail);

  const login = async (emailOrPersonalIdValue: string, password: string) => {
    const data = await fetchBasicToken({
      emailOrPersonalId: emailOrPersonalIdValue,
      password,
      fingerprint,
    });
    setEmailOrPersonalId(emailOrPersonalIdValue);

    // 2faOptionalComment: Remove changes related 2fa options when BE deployment is done on PROD [zaquiel]
    if (data.token) {
      setLoginStateData(data);
    } else {
      setVerificationId(data.verificationId);
    }

    return data;
  };

  const logout = useCallback(async () => {
    await deleteTokenMutation.mutateAsync();
    setIsLoggedIn(false);
    setIsForbidden(false);
    sessionStorage.clear();
    localStorage.clear();
  }, []);

  useEffect(() => {
    if (
      deleteTokenMutation.status === 'success' ||
      deleteTokenMutation.status === 'error'
    ) {
      setToken(null);
      setEmailOrPersonalId(null);
      setFromTemporaryPassword(false);
      queryClient.invalidateQueries();
    }
  }, [deleteTokenMutation.status]);

  const recoverByEmail = useCallback((email: string) => {
    passwordRecoveryMutation.exec({ email });
  }, []);

  const triggerUnauthorized = () => {
    logout();
  };

  const triggerForbidden = () => {
    setIsForbidden(true);
  };

  // START 2FA BLOCK
  const getSession2faTime = () => sessionStorage.getItem(SESSION_2FA_TIME_KEY);

  const [secondsLeft2fa, setSecondsLeft2fa] = useState(0);

  // reset timer and update ref
  useEffect(() => {
    refStatus2faEmail.current = status2faEmail;

    if (status2faEmail === 'error') {
      reset2faCode();
      setSecondsLeft2fa(0);
    }
  }, [status2faEmail]);

  const updateSecondsLeft = () => {
    const session2faTime = getSession2faTime();
    if (!session2faTime) return;
    const now = dayjs();
    const secondsPassed = now.diff(session2faTime, 'second');
    if (
      secondsPassed >= SECONDS_GAP_BETWEEN_CALLS ||
      refStatus2faEmail.current === 'error'
    ) {
      sessionStorage.removeItem(SESSION_2FA_TIME_KEY);
      setSecondsLeft2fa(0);
    } else {
      setSecondsLeft2fa(
        SECONDS_GAP_BETWEEN_CALLS - now.diff(session2faTime, 'second'),
      );
    }
  };

  useEffect(() => {
    setInterval(() => {
      if (
        !getSession2faTime() ||
        refStatus2faEmail.current === 'error' ||
        !secondsLeft2fa
      )
        return;
      updateSecondsLeft();
    }, 1000);
  }, [secondsLeft2fa]);

  // Send initial email
  useEffect(() => {
    if (verificationId && !twoFACode) {
      fetch2faEMAIL();
    }
  }, [verificationId]);

  const clear2faStates = () => {
    // reset states
    setTwoFACode(null);
    setVerificationId(null);
    // reset mutation
    reset2faEmail();
    reset2faCode();
  };

  const fetch2faEMAIL = async () => {
    sessionStorage.setItem(SESSION_2FA_TIME_KEY, dayjs().toString());
    setSecondsLeft2fa(SECONDS_GAP_BETWEEN_CALLS);
    if (verificationId) {
      const { codeId } = await mutateAsyncEMAIL({
        verificationId,
      });
      setTwoFACode(codeId);
    }
  };

  const setLoginStateData = (data: post2faCodeResponse) => {
    if (
      !data.username ||
      !data.token ||
      data.fromTemporaryPassword === undefined
    )
      return;
    setIsLoggedIn(true);
    setToken(createAuthToken(data.username, data.token));
    setFromTemporaryPassword(data.fromTemporaryPassword);
  };

  const fetch2faCode = async (code: string) => {
    const data = await mutateAsyncCode({
      verificationId: verificationId || '',
      codeId: twoFACode || '',
      code,
      fingerprint,
    });
    // reset 2fa state variables
    clear2faStates();
    setLoginStateData(data);
  };

  // END 2FA BLOCK

  const authContextValue: AuthContextValue = {
    login,
    logout,
    triggerUnauthorized,
    triggerForbidden,
    recoverByEmail,
    recoverPasswordStatus: passwordRecoveryMutation.status,
    loggingInStatus: basicTokenStatus,
    loginError,
    isLoggedIn,
    fromTemporaryPassword,
    emailOrPersonalId,
    token,
    isForbidden,
    verificationId,
    // 2fa
    secondsLeft2fa,
    fetch2faEMAIL,
    fetch2faCode,
    error2faCode: (error2faCode as ValidationError | undefined)
      ?.fieldErrors?.[0]?.property as TYPE_ERROR_CODE_EMAIL_MESSAGE | undefined,
    status2faCode,
    clear2faStates,
    error2faEmail: (error2faEmail as ValidationError | undefined)
      ?.fieldErrors?.[0]?.property as TYPE_ERROR_CODE_EMAIL_MESSAGE | undefined,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

const createAuthToken = (username: string, token: string): string =>
  window.btoa(`${username}:${token}`);
